import * as React from "react";
import { Polygon } from "geojson";
import { useApi } from "../containers/ApiContainer";
import { uuidv4 } from "../utils";

export type TowingEquipmentAdmin = {
  id: string;
  name: string;
  width: number;
  length: number;
  height: number;
  tow_bar_length?: number | null;
  no_footprint?: boolean;
  geom?: Polygon;
  geomz?: Polygon;
  created_at?: string;
};

export const useTowingEquipmentAdmin = () => {
  const { postgrest } = useApi();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [towingEquipment, setTowingEquipment] = React.useState<
    TowingEquipmentAdmin[]
  >([]);

  const fetchTowingEquipment = React.useCallback(async () => {
    try {
      setError(null);
      const { data, error: fetchError } = await postgrest
        .from("towing_equipment")
        .select()
        .order("name");

      if (fetchError) {
        throw fetchError;
      }

      setTowingEquipment(data || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching towing equipment:", err);
      setError(err?.message || "Failed to fetch towing equipment");
      setLoading(false);
    }
  }, [postgrest]);

  React.useEffect(() => {
    fetchTowingEquipment();
  }, [fetchTowingEquipment]);

  const upsertTowingEquipment = React.useCallback(
    async (equipment: TowingEquipmentAdmin) => {
      try {
        setError(null);

        // Call the admin upsert function
        const { data, error: rpcError } = await postgrest.rpc(
          "admin_upsert_towing_equipment",
          {
            p_id: equipment.id,
            p_name: equipment.name,
            p_width: equipment.width,
            p_length: equipment.length,
            p_height: equipment.height,
            p_tow_bar_length: equipment.tow_bar_length,
            p_no_footprint: equipment.no_footprint || false
          }
        );

        if (rpcError) {
          throw rpcError;
        }

        // Refresh the list
        await fetchTowingEquipment();

        return data;
      } catch (err) {
        console.error("Error upserting towing equipment:", err);
        setError(err?.message || "Failed to save towing equipment");
        throw err;
      }
    },
    [postgrest, fetchTowingEquipment]
  );

  const createTowingEquipment = React.useCallback(
    async (equipment: Omit<TowingEquipmentAdmin, "id">) => {
      const newEquipment: TowingEquipmentAdmin = {
        ...equipment,
        id: uuidv4(),
        created_at: new Date().toISOString(),
      };

      const id = await upsertTowingEquipment(newEquipment);
      return { ...newEquipment, id: id || newEquipment.id };
    },
    [upsertTowingEquipment]
  );

  const deleteTowingEquipment = React.useCallback(
    async (id: string) => {
      try {
        setError(null);

        // Call the admin delete function
        const { data, error: rpcError } = await postgrest.rpc(
          "admin_delete_towing_equipment",
          { p_id: id }
        );

        if (rpcError) {
          throw rpcError;
        }

        // Refresh the list
        await fetchTowingEquipment();

        return data;
      } catch (err) {
        console.error("Error deleting towing equipment:", err);
        setError(err?.message || "Failed to delete towing equipment");
        throw err;
      }
    },
    [postgrest, fetchTowingEquipment]
  );

  const updateTowingEquipment = React.useCallback(
    async (equipment: TowingEquipmentAdmin) => {
      return upsertTowingEquipment(equipment);
    },
    [upsertTowingEquipment]
  );

  return {
    loading,
    error,
    towingEquipment,
    createTowingEquipment,
    updateTowingEquipment,
    deleteTowingEquipment,
    refreshTowingEquipment: fetchTowingEquipment,
  };
};
